import React, { useState } from 'react';
import SwSelect from '../Common/SwReactSelect/SwReactSelect';
import { useEffect } from 'react';
import axios from 'axios';
import './css/ExtendedV2.css';
import SwButton from '../Common/SwButtons/SwButtons';
import SwIconButton from '../Common/SwIconButton/SwIconButton';
import { Tag } from 'antd';
import SwOffCanvas from '../Common/SwOffCanvas/SwOffCanvas';
import AntDDrawer from '../Common/AntDDrawer/AntDDrawer';
import SwModal from '../Common/SwModal/SwModal';
import SwAlert from '../Common/SwAlert/SwAlert';
import AntDDatePicker from '../Common/AntDDateTimePicker/AntDDatePicker';
import AntDTimePicker from '../Common/AntDDateTimePicker/AntDTimePicker';
import AntDTable from '../Common/AntDTable/AntDTable';
import SwNoData from '../Common/NoData/NoData';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from "react-toastify";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SwTextArea from '../Common/FormComponent/SwTextArea';
import SwInput from '../Common/FormComponent/SwInput';
import Form from "react-bootstrap/Form";
import DevicePicker from './DevicePicker';
dayjs.extend(customParseFormat);

// Utility: Check if a given date + time is in the past
const isPastDateTime = (date, time) => {
  if (!date || !time) return false;
  const combined = date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0);
  return combined.isBefore(dayjs());
};

/**
 * Zone picker rendered as clickable CARDS (not bare radios) so the whole card area
 * toggles selection — matching the nexus-ops booking wizard. Shows the zone name over a
 * meta line ("N devices · schedule"). `zones` may carry deviceIds or deviceCount; count
 * is shown only when available.
 */
const ZoneCardSelector = ({ zones, selectedZoneId, onSelect, zoneSchedules }) => (
  <div className="EWH-ZoneGrid">
    {zones.map((z) => {
      const selected = selectedZoneId === z.zoneId;
      const sched = zoneSchedules?.[z.zoneId];
      const count = Array.isArray(z.deviceIds)
        ? z.deviceIds.length
        : (typeof z.deviceCount === 'number' ? z.deviceCount : null);
      const meta = [
        count != null ? `${count} device${count !== 1 ? 's' : ''}` : null,
        sched || null,
      ].filter(Boolean).join(' · ');
      return (
        <div
          key={z.zoneId}
          role="button"
          tabIndex={0}
          className={`EWH-ZoneCard${selected ? ' is-selected' : ''}`}
          onClick={() => onSelect(z.zoneId)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(z.zoneId); } }}
        >
          <span className="EWH-ZoneCard-Dot" />
          <span className="EWH-ZoneCard-Body">
            <span className="EWH-ZoneCard-Name">{z.zoneName || 'Untitled'}</span>
            {meta ? <span className="EWH-ZoneCard-Meta">{meta}</span> : null}
          </span>
          <span className={`EWH-ZoneCard-Radio${selected ? ' is-selected' : ''}`} />
        </div>
      );
    })}
  </div>
);

const ExtendedV2 = () => {
  const [locationValue, setLocationValue] = useState(null);
  const [floorValue, setFloorValue] = useState(null);

  // ── Per-device booking (ACs chargeable + Lights free) ────────────────────
  // Selecting nothing books the whole floor (previous behaviour).
  const [bookableAcs, setBookableAcs] = useState([]);
  const [bookableLights, setBookableLights] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [selectedAcIds, setSelectedAcIds] = useState([]);
  const [selectedLightIds, setSelectedLightIds] = useState([]);

  // ── Single-zone booking ──────────────────────────────────────────────────
  // A zoned floor must be booked ONE zone at a time (mirrors nexus-api's rule +
  // the per-zone schedule-overlap check). `zones` holds the client's own bookable
  // zones on the selected floor; the booking targets exactly one of them.
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const selectedZone = zones.find((z) => z.zoneId === selectedZoneId) || null;
  // zoneId → resolved schedule label for the booking date, shown next to each zone radio
  // so the operator sees each zone's hours before choosing one.
  const [zoneSchedules, setZoneSchedules] = useState({});
  // Billing config for the client-floor — drives how the DevicePicker prices the booking
  // (flat → day's flat rate, submeter → metered, ac-based → per-AC). Display only.
  const [powerRate, setPowerRate] = useState(null); // { principle, flat: {...} }
  // The booked zone's own flat rate (flat + zone-based floors), from bookable-devices.
  const [zoneFlatRate, setZoneFlatRate] = useState(null);

  const [defaultStartTime, setDefaultStartTime] = useState(null);
const [defaultEndTime, setDefaultEndTime] = useState(null);
const [maxEndTime, setMaxEndTime] = useState(null);

const [scheduleEndTime, setScheduleEndTime] = useState(null);
const [noDefaultSchedule, setNoDefaultSchedule] = useState(false);
// AC/Lights split: AC's own default window (lights window lives in defaultStart/EndTime).
const [acDefaultSchedule, setAcDefaultSchedule] = useState(null);

// Today's default schedule (for Instant ON disable check)
const [todayScheduleStart, setTodayScheduleStart] = useState(null);
const [todayScheduleEnd, setTodayScheduleEnd] = useState(null);

  const [locationOptions, setLocationOptions] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);

  // Schedule OffCanvas State
  const [scheduleBooking, setScheduleBooking] = useState(false);

  // Schedule Booking Date and Time State
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleStartTime, setScheduleStartTime] = useState(null);

  const handleScheduleStartTimeChange = (timeObj, timeString) => {
    console.log(timeObj, timeString);
    setScheduleStartTime(timeObj);
    // Reset end time if it's no longer valid (end must be after start)
    // 12:00 AM (midnight) stays valid if new start is still PM
    if (timeObj && scheduleEndTime) {
      const isMidnight = scheduleEndTime.hour() === 0 && scheduleEndTime.minute() === 0;
      // 12:00 AM always valid as end-of-day — never reset it
      if (!isMidnight && !scheduleEndTime.isAfter(timeObj)) {
        setScheduleEndTime(null);
      }
    }
  };
  const handleScheduleEndTimeChange = (timeObj, timeString) => {
    console.log(timeObj, timeString);
    if (!timeObj) { setScheduleEndTime(null); return; }
    if (!scheduleStartTime) { setScheduleEndTime(timeObj); return; }
    const isMidnight = timeObj.hour() === 0 && timeObj.minute() === 0;
    // 12:00 AM always valid as end-of-day regardless of start time
    if (!isMidnight && !timeObj.isAfter(scheduleStartTime)) {
      toast.error("End time must be after Start time");
      return;
    }
    setScheduleEndTime(timeObj);
  };

  // Confirm Schedule Booking Modal State
  const [confirmScheduleBooking, setConfirmScheduleBooking] = useState(false);

  // Post-booking success modal state (Fix #6: show Request ID after booking)
  const [scheduleSuccessModal, setScheduleSuccessModal] = useState(false);
  const [scheduleResult, setScheduleResult] = useState(null);

  // Instant On Modal State
  const [instantOn, setInstantOn] = useState(false);

  // Instatnt Floor State and options
  const [instantOnFloor, setInstantOnFloor] = useState(null);

  // Holidays List Modal State
  const [holidaysModalOpen, setHolidaysModalOpen] = useState(false);
  const [holidaysList, setHolidaysList] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);

  const fetchHolidays = async () => {
    const centreId = locationValue?.locationId;
    if (!centreId) return;

    setHolidaysLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_GATEWAYS_URL}cspoc/api/v2/basic-data/holidays/${centreId}`
      );
      const list = response?.data?.data || response?.data || [];
      setHolidaysList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error(error?.response?.data?.message || "Failed to fetch holidays list");
      setHolidaysList([]);
    } finally {
      setHolidaysLoading(false);
    }
  };

  const handleHolidaysListOpen = () => {
    setHolidaysModalOpen(true);
    fetchHolidays();
  };

  const holidaysColumns = [
    {
      title: 'Date',
      key: 'date',
      render: (_, record) => {
        const rawDate = record.date || record.holidayDate || record.holiday_date;
        return rawDate ? dayjs(rawDate).format('DD MMM YYYY') : '-';
      },
    },
    {
      title: 'Day',
      key: 'day',
      render: (_, record) => {
        const rawDate = record.date || record.holidayDate || record.holiday_date;
        return record.dayOfWeek || record.day || (rawDate ? dayjs(rawDate).format('dddd') : '-');
      },
    },
    {
      title: 'Holiday Name',
      key: 'name',
      render: (_, record) =>
        record.name || record.holidayName || record.holiday_name || record.title || record.description || '-',
    },
  ];

  const fetchBuildingData = async () => {
    try {
      const headData = JSON.parse(localStorage.getItem('headData'));
    const newCompanyId = JSON.parse(localStorage.getItem('headData'));
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_GATEWAYS_URL}cspoc/api/v2/basic-data/get-organization-by-spoc`,
        {
          params: {
            spoc: headData.rec_id,
            client: headData.company_id,
            clientCoreId:newCompanyId.organizationId,

          },
        }
      );

      const organizations = response?.data?.data || [];

      const buildings = organizations.flatMap(org =>
        (org.properties || []).map(property => ({
          value: property.crm_id,
          label: property.name,
          floors: property.floors || [],
          locationId: property.crm_id,
          clientId: org.crm_id,
          clientName: org.name,
        }))
      );

      setLocationOptions(buildings);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };




// Load the client's zones on the selected floor. A zoned floor must be booked one
// zone at a time; 24×7 zones have no free window so they're excluded. Auto-selects
// when there is exactly one bookable zone.
useEffect(() => {
  const clientId = locationValue?.clientId;
  const floorId = floorValue?.value;
  const centerId = locationValue?.locationId;

  setSelectedZoneId(null);

  if (!clientId || !floorId || !centerId) {
    setZones([]);
    return;
  }

  let cancelled = false;
  axiosInstance
    .get(`${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/floor-zones`, {
      params: { centerId, floorId },
    })
    .then((res) => {
      if (cancelled) return;
      const all = Array.isArray(res?.data?.data) ? res.data.data : [];
      const clientZones = all.filter(
        (z) => z.clientId && String(z.clientId) === String(clientId) && !z.is24x7,
      );
      setZones(clientZones);
      if (clientZones.length === 1) setSelectedZoneId(clientZones[0].zoneId);
    })
    .catch(() => {
      // Non-fatal: fall back to whole-floor booking (no zone radio shown).
      if (cancelled) return;
      setZones([]);
    });

  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [locationValue?.clientId, locationValue?.locationId, floorValue?.value]);

// Load the bookable devices (chargeable ACs + free lights) for the exact area being
// booked — narrowed to the selected zone when the floor is zoned. Selection is reset
// so a stale pick can't leak across zones/floors.
useEffect(() => {
  const clientId = locationValue?.clientId;
  const floorId = floorValue?.value;
  const centreId = locationValue?.locationId;
  const zoneId = selectedZoneId;

  setSelectedAcIds([]);
  setSelectedLightIds([]);
  setZoneFlatRate(null);
  setPowerRate(null);

  if (!clientId || !floorId) {
    setBookableAcs([]);
    setBookableLights([]);
    // Clear any loader from a prior run — otherwise the picker spins forever.
    setDevicesLoading(false);
    return;
  }
  // Zoned floor: wait for a zone pick before loading its devices. Clear the loader
  // too: the first run (before zones loaded) started a whole-floor fetch that gets
  // cancelled here, and a cancelled fetch's .finally never resets the flag.
  if (zones.length > 0 && !zoneId) {
    setBookableAcs([]);
    setBookableLights([]);
    setDevicesLoading(false);
    return;
  }

  let cancelled = false;
  setDevicesLoading(true);
  axiosInstance
    .get(`${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/bookable-devices`, {
      params: { clientId, floorId, centreId, ...(zoneId ? { zoneId } : {}) },
    })
    .then((res) => {
      if (cancelled) return;
      const data = res?.data?.data || {};
      setBookableAcs(Array.isArray(data.acs) ? data.acs : []);
      setBookableLights(Array.isArray(data.lights) ? data.lights : []);
      setZoneFlatRate(data.zoneFlatRate ?? null);
      // Billing principle + floor flat rate come from THIS (SPOC-reachable) endpoint. The
      // /ops/power-config GET is Ops/TOps role-gated and 403s for a SPOC token, which left
      // billingPrinciple null → the picker showed per-AC ₹ + an ac-based estimate even on
      // submeter/flat floors. `flat` here is the FLOOR flat rate; zoneFlatRate overrides it
      // per zone in flatRateFor/activeFlat.
      setPowerRate({ principle: data.billingPrinciple ?? null, flat: data.floorFlatRate ?? null });
    })
    .catch(() => {
      // Non-fatal: fall back to whole-floor booking (no device picker rows).
      if (cancelled) return;
      setBookableAcs([]);
      setBookableLights([]);
    })
    .finally(() => {
      if (!cancelled) setDevicesLoading(false);
    });

  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [locationValue?.clientId, locationValue?.locationId, floorValue?.value, selectedZoneId, zones.length]);

/**
 * Reset the booking form's selections so a modal always opens fresh — the picked
 * zone, date/time, device selection and the resolved default-schedule info. Called on
 * every modal exit (Cancel / X / after a successful booking). `bookableAcs/Lights`
 * self-recompute via the device-load effect. Zone resets to the sole zone when the floor
 * has exactly one (mirrors the auto-select in the zones-loading effect), else null.
 */
const resetBookingForm = () => {
  setSelectedZoneId(zones.length === 1 ? zones[0].zoneId : null);
  setSelectedAcIds([]);
  setSelectedLightIds([]);
  setScheduleDate(null);
  setScheduleStartTime(null);
  setScheduleEndTime(null);
  setNoDefaultSchedule(false);
  setDefaultStartTime(null);
  setDefaultEndTime(null);
  setMaxEndTime(null);
  setAcDefaultSchedule(null);
  setTodayScheduleStart(null);
  setTodayScheduleEnd(null);
  setInstantOffAtTime(null);
};

/** Billable hours between two dayjs times — min 1, part-hours round up (matches API). */
const billableHoursBetween = (start, end) => {
  if (!start || !end) return 1;
  const endMin = end.hour() === 0 && end.minute() === 0 ? 24 * 60 : end.hour() * 60 + end.minute();
  const diff = endMin - (start.hour() * 60 + start.minute());
  return diff <= 0 ? 1 : Math.max(1, Math.ceil(diff / 60));
};

const fetchClientSchedule = async (selectedDate) => {
  try {
    const clientId = locationValue?.clientId;
    const floorId = floorValue?.value;

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/client-schedule/${clientId}/${floorId}`,
      {
        params: {
          date: selectedDate.format("YYYY-MM-DD"),
          spaceType: 'CLIENT_SPACE',
          // Zoned client-floor → the selected zone's own schedule (not the whole-floor default).
          ...(selectedZoneId ? { zoneId: selectedZoneId } : {}),
        },
      }
    );

    const data = response?.data?.data;
    const schedule = data?.daySchedule;

    if (schedule?.isOff) {
      setDefaultStartTime(null);
      setDefaultEndTime(null);
      setNoDefaultSchedule(true);
    } else if (schedule?.is24x7) {
      setDefaultStartTime('00:00');
      setDefaultEndTime('23:59');
      setNoDefaultSchedule(false);
    } else if (schedule?.startTime && schedule?.endTime) {
      setDefaultStartTime(schedule.startTime);
      setDefaultEndTime(schedule.endTime);
      setNoDefaultSchedule(false);
    } else {
      setDefaultStartTime(null);
      setDefaultEndTime(null);
      setNoDefaultSchedule(true);
    }

    // AC/Lights split: the AC keeps different hours than the lights (daySchedule above
    // is the lights side). Capture the AC window so the wizard can show + block it too.
    setAcDefaultSchedule(data?.scheduleMode === 'ac-lights-split' ? (data?.acDaySchedule ?? null) : null);

  } catch (error) {
    console.error("Schedule API Error:", error);
    setDefaultStartTime(null);
    setDefaultEndTime(null);
    setNoDefaultSchedule(true);
    setAcDefaultSchedule(null);
  }
};
// Fetch EACH zone's resolved schedule for a date, for the per-zone labels on the zone
// radios. Best-effort: a failed zone just shows no label. Reuses the per-zone endpoint.
const fetchAllZoneSchedules = async (dateObj) => {
  try {
    const clientId = locationValue?.clientId;
    const floorId = floorValue?.value;
    if (!clientId || !floorId || !dateObj || zones.length === 0) {
      setZoneSchedules({});
      return;
    }
    const dateStr = dayjs(dateObj).format("YYYY-MM-DD");
    const fmt = (day) => {
      if (!day) return "";
      if (day.isOff) return "Off this day";
      if (day.is24x7) return "24/7";
      if (!day.startTime || !day.endTime) return "";
      const s = dayjs(day.startTime, "HH:mm").isValid() ? dayjs(day.startTime, "HH:mm").format("h:mm A") : day.startTime;
      const e = dayjs(day.endTime, "HH:mm").isValid() ? dayjs(day.endTime, "HH:mm").format("h:mm A") : day.endTime;
      return `${s} – ${e}`;
    };
    const entries = await Promise.all(
      zones.map(async (z) => {
        try {
          const response = await axiosInstance.get(
            `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/client-schedule/${clientId}/${floorId}`,
            { params: { date: dateStr, spaceType: "CLIENT_SPACE", zoneId: z.zoneId } }
          );
          return [z.zoneId, fmt(response?.data?.data?.daySchedule)];
        } catch {
          return [z.zoneId, ""];
        }
      })
    );
    setZoneSchedules(Object.fromEntries(entries));
  } catch {
    setZoneSchedules({});
  }
};

// Refresh per-zone schedule labels whenever the zone set or the chosen date changes.
// The Instant view has no date (scheduleDate null) → use today.
useEffect(() => {
  fetchAllZoneSchedules(scheduleDate || dayjs());
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [zones, scheduleDate, locationValue?.clientId, floorValue?.value]);

// NOTE: billing principle + floor flat rate are now sourced from the SPOC-reachable
// /api/ops/properties/bookable-devices response (see the devices effect above) and set on
// `powerRate` there. The /ops/power-config GET is Ops/TOps role-gated, so a SPOC token can
// never read it — fetching it here 403'd and left billingPrinciple null, which made the
// picker show per-AC ₹ + an ac-based estimate even on submeter/flat floors.

const billingPrinciple = powerRate?.principle ?? null;
// The flat-rate config in force for the current selection: a zone booking on a flat floor
// prices by the zone's OWN flat rate when set, else the floor's. Drives both the per-day
// estimate and the "Default schedule" info banner (so the banner is zone-aware too).
const activeFlat = (selectedZoneId && zoneFlatRate) ? zoneFlatRate : powerRate?.flat;
// Format a "HH:mm" time to 12-hour "h:mm A" for display (falls back to the raw value / em-dash).
const to12 = (t) => (t && dayjs(t, "HH:mm").isValid() ? dayjs(t, "HH:mm").format("h:mm A") : (t || "—"));
// Flat rate/hour for a given date (weekday/weekend/per-day override) — mirrors the
// backend resolveFlatRate (holiday handling stays server-side).
const flatRateFor = (dateObj) => {
  if (billingPrinciple !== "flat" || !dateObj) return null;
  const flat = activeFlat;
  if (!flat) return null;
  const dow = dayjs(dateObj).day(); // 0=Sun..6=Sat
  const ovr = (flat.perDayOverride ?? []).find((o) => o.dayOfWeek === dow);
  if (ovr && ovr.ratePerHour != null) return ovr.ratePerHour;
  const isWeekend = dow === 0 || dow === 6;
  const r = isWeekend ? flat.weekendRatePerHour : flat.weekdayRatePerHour;
  return r ?? null;
};

const fetchTodaySchedule = async () => {
  try {
    const clientId = locationValue?.clientId;
    const floorId = floorValue?.value;
    if (!clientId || !floorId) return;

    const response = await axiosInstance.get(
      `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/client-schedule/${clientId}/${floorId}`,
      {
        params: {
          date: dayjs().format("YYYY-MM-DD"),
          spaceType: 'CLIENT_SPACE',
          ...(selectedZoneId ? { zoneId: selectedZoneId } : {}),
        },
      }
    );

    const data = response?.data?.data;
    const schedule = data?.daySchedule;

    if (schedule?.isOff) {
      setTodayScheduleStart(null);
      setTodayScheduleEnd(null);
    } else if (schedule?.is24x7) {
      setTodayScheduleStart('00:00');
      setTodayScheduleEnd('23:59');
    } else if (schedule?.startTime && schedule?.endTime) {
      setTodayScheduleStart(schedule.startTime);
      setTodayScheduleEnd(schedule.endTime);
    } else {
      setTodayScheduleStart(null);
      setTodayScheduleEnd(null);
    }
  } catch (error) {
    console.error("Today schedule fetch error:", error);
    setTodayScheduleStart(null);
    setTodayScheduleEnd(null);
  }
};

// Check if current time falls within today's default schedule
const isWithinDefaultSchedule = () => {
  if (!todayScheduleStart || !todayScheduleEnd) return false;
  const now = dayjs();
  const start = dayjs(todayScheduleStart, "hh:mm A").isValid()
    ? dayjs(todayScheduleStart, "hh:mm A")
    : dayjs(todayScheduleStart, "HH:mm");
  const end = dayjs(todayScheduleEnd, "hh:mm A").isValid()
    ? dayjs(todayScheduleEnd, "hh:mm A")
    : dayjs(todayScheduleEnd, "HH:mm");
  return now.isAfter(start) && now.isBefore(end);
};

// Check if there's an active booking for the selected floor right now
const hasActiveBookingForFloor = () => {
  if (!floorValue) return false;
  const now = dayjs();
  return bookingsList.some((b) => {
    if (b.cancelStatus) return false;
    if (b.floorId !== floorValue.value && b.floorName !== floorValue.label) return false;
    const start = parseBookingTime(b.date, b.startTime);
    const end = parseBookingTime(b.date, b.endTime);
    return now.isAfter(start) && now.isBefore(end);
  });
};

// Combined check: Instant ON should be disabled if within default schedule OR active booking exists
const isInstantOnDisabled = () => {
  return isWithinDefaultSchedule() || hasActiveBookingForFloor();
};

// Get reason why Instant ON is disabled
const getInstantOnDisabledReason = () => {
  if (isWithinDefaultSchedule()) {
    return `Instant ON is disabled — the device is currently operating under the Default Schedule (${todayScheduleStart} - ${todayScheduleEnd}).`;
  }
  if (hasActiveBookingForFloor()) {
    return "Instant ON is disabled — there is already an active booking for this floor.";
  }
  return "";
};

 const handleLocationChange = selectedLocation => {
  setLocationValue(selectedLocation);
  setFloorValue(null);

  if (selectedLocation?.floors) {
  const mappedFloors = selectedLocation.floors.map(floor => ({
  value: floor.crm_id,   // ✅ USE CRM ID
  label: floor.name,
}));

    setFloorOptions(mappedFloors);
  }
};

  useEffect(() => {
    fetchBuildingData();
  }, []);






const handleConfirmSchedule = async () => {
  try {
    setConfirmLoading(true);
    const authToken = JSON.parse(localStorage.getItem("headData"));

    const payload = {
      locationId: locationValue?.locationId,
      clientId: locationValue?.clientId,
      clientName: locationValue?.clientName,
      floorId: floorValue?.value,
      floorName: floorValue?.label,
      date: scheduleDate?.format("YYYY-MM-DD"),
      startTime: scheduleStartTime
        ? (scheduleStartTime.hour() === 0 && scheduleStartTime.minute() === 0 ? "00:00:01" : `${scheduleStartTime.format("HH:mm")}:00`)
        : undefined,
      endTime: scheduleEndTime
        ? (scheduleEndTime.hour() === 0 && scheduleEndTime.minute() === 0 ? "23:59:59" : `${scheduleEndTime.format("HH:mm")}:00`)
        : undefined,
      source:'cspoc',
      // Single-zone booking — sent when the floor is zoned.
      ...(selectedZoneId ? { zoneId: selectedZoneId, zoneName: selectedZone?.zoneName } : {}),
      // Per-device booking — omitted when nothing is picked (books the whole zone/floor).
      ...(selectedAcIds.length ? { selectedAcIds } : {}),
      ...(selectedLightIds.length ? { selectedLightIds } : {}),
    };

    const response = await axiosInstance.post(
      `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/extended-hours`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${authToken?.spintlyToken}`,
        },
      }
    );

    // ✅ SUCCESS FLOW — show confirmation modal with Request ID
    const resultData = response?.data?.data || response?.data;
    setScheduleResult({
      ...resultData,
      floorName: floorValue?.label,
      date: scheduleDate?.format("YYYY-MM-DD"),
      startTime: scheduleStartTime?.format("HH:mm"),
      endTime: scheduleEndTime?.format("HH:mm"),
    });

    setConfirmLoading(false);

    // Close booking modals, open success modal
    setConfirmScheduleBooking(false);
    setScheduleBooking(false);
    setScheduleSuccessModal(true);

    // Reset the whole form so the next open starts clean.
    resetBookingForm();

    // Refresh bookings list
    fetchBookingsList();

  } catch (error) {
    console.error(error);
    setConfirmLoading(false);
    toast.error(
      error?.response?.data?.message || "Something went wrong"
    );
  }
};

  // Instant Off At Time State
  const [instantOffAtTime, setInstantOffAtTime] = useState(null);
  const handleInstantOffAtTimeChange = (timeObj, timeString) => {
    console.log(timeObj, timeString);
    if (!timeObj) { setInstantOffAtTime(null); return; }
    const now = dayjs();
    const isMidnight = timeObj.hour() === 0 && timeObj.minute() === 0;
    const nowMin = now.hour() * 60 + now.minute();
    const selMin = timeObj.hour() * 60 + timeObj.minute();
    // 12:00 AM always valid as end-of-day; compare time-of-day only to avoid date mismatch
    if (!isMidnight && selMin <= nowMin) {
      toast.error("End time must be after current time");
      return;
    }
    setInstantOffAtTime(timeObj);
  };

  // Bookings List State
  const [bookingsList, setBookingsList] = useState([]);

  const fetchBookingsList = async () => {
    try {
      const locationId = locationValue?.locationId;

      const clientId1 = JSON.parse(localStorage.getItem('headData'));
      const clientId=clientId1.clientCrmId

      if (!locationId) return;

      const now = dayjs();
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/extended-hours/location/${locationId}`,
        {
          params: {
            month: now.month() + 1,
            year: now.year(),
            page: 1,
            limit: 500,
            clientId
          },
        }
      );

      const bookings = response?.data?.data || [];
      console.log("📋 Bookings API Response:", JSON.stringify(bookings, null, 2));
      setBookingsList(bookings);
    } catch (error) {
      console.error("Error fetching bookings list:", error);
    }
  };

  // Helper: parse time string in multiple formats (HH:mm, hh:mm A, h:mm A)
  const parseBookingTime = (dateStr, timeStr) => {
    const bookingDate = dayjs(dateStr).format("YYYY-MM-DD");
    // Try 24-hour format first
    let parsed = dayjs(`${bookingDate} ${timeStr}`, "YYYY-MM-DD HH:mm");
    if (parsed.isValid()) return parsed;
    // Try 12-hour format
    parsed = dayjs(`${bookingDate} ${timeStr}`, "YYYY-MM-DD hh:mm A");
    if (parsed.isValid()) return parsed;
    parsed = dayjs(`${bookingDate} ${timeStr}`, "YYYY-MM-DD h:mm A");
    if (parsed.isValid()) return parsed;
    return dayjs(`${bookingDate} ${timeStr}`);
  };

  // Derive active and upcoming bookings from list (Fix #2: support multiple active)
  const getActiveBookings = () => {
    const now = dayjs();
    return bookingsList.filter((b) => {
      if (b.cancelStatus) return false;
      if (b.status === "ENDED_EARLY") return false;
      if (b.source !== "cspoc" && b.bookingReason !== "Client request") return false;
      const start = parseBookingTime(b.date, b.startTime);
      const end = parseBookingTime(b.date, b.endTime);
      return now.isAfter(start) && now.isBefore(end);
    });
  };

  const getUpcomingBookings = () => {
    const now = dayjs();
    return bookingsList.filter((b) => {
      if (b.source !== "cspoc" && b.bookingReason !== "Client request") return false;
      const start = parseBookingTime(b.date, b.startTime);
      return start.isAfter(now);
    });
  };
  

  const formatBookingTime = (time) => {
    // Try multiple formats
    let parsed = dayjs(time, "HH:mm");
    if (!parsed.isValid()) parsed = dayjs(time, "hh:mm A");
    if (!parsed.isValid()) parsed = dayjs(time, "h:mm A");
    return parsed.isValid() ? parsed.format("h:mm A") : time;
  };

  const getRemainingTime = (booking) => {
    const end = parseBookingTime(booking.date, booking.endTime);
    const diff = end.diff(dayjs(), "minute");
    if (diff <= 0) return "";
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}h ${mins}m remaining`;
  };

  const isCancellable = (booking) => {
    const start = parseBookingTime(booking.date, booking.startTime);
    return start.diff(dayjs(), "hour") >= 24;
  };


  // Fix #8: Check for overlapping bookings on same floor/date/time
  const hasOverlappingBooking = (date, startTime, endTime, floorId) => {
    const newStart = parseBookingTime(date.format("YYYY-MM-DD"), startTime.format("HH:mm"));
    let newEnd = parseBookingTime(date.format("YYYY-MM-DD"), endTime.format("HH:mm"));
    if (endTime.hour() === 0 && endTime.minute() === 0) {
      newEnd = newEnd.add(1, "day");
    }
    return bookingsList.find((b) => {
      if (b.cancelStatus) return false;
      if (b.floorId !== floorId && b.floorName !== floorValue?.label) return false;
      // Zone-scoped: only conflict with bookings for the same zone (mirrors backend).
      // A floor-level booking (no zoneId) still conflicts with any zone.
      if (selectedZoneId && b.zoneId && b.zoneId !== selectedZoneId) return false;
      // Device-class scoped (#3): AC-only and Light-only bookings don't cross-block.
      // A legacy booking with neither recorded is treated as covering both.
      {
        const exHasAc = Array.isArray(b.selectedAcIds) && b.selectedAcIds.length > 0;
        const exHasLight = Array.isArray(b.selectedLightIds) && b.selectedLightIds.length > 0;
        const exBoth = !exHasAc && !exHasLight;
        const newHasAc = selectedAcIds.length > 0;
        const newHasLight = selectedLightIds.length > 0;
        // No explicit device pick = whole-floor booking → covers BOTH classes, so it
        // conflicts with everything on the scope (mirrors the nexus-api overlap check).
        const newBoth = !newHasAc && !newHasLight;
        const sharesClass = newBoth || (newHasAc && (exHasAc || exBoth)) || (newHasLight && (exHasLight || exBoth));
        if (!sharesClass) return false;
        // Per-device relaxation (#item2): AC-based/submeter floors allow two bookings on the
        // same scope+time when they don't book the SAME device (both must pick specific
        // devices; a whole-scope booking still blocks). Mirrors the nexus-api overlap check.
        // Device-scoped = every floor except explicit FLAT (unconfigured → device-scoped, matches billing).
        const deviceScoped = billingPrinciple !== "flat";
        if (deviceScoped && (newHasAc || newHasLight) && (exHasAc || exHasLight)) {
          const exAc = (b.selectedAcIds || []).map(String);
          const exLight = (b.selectedLightIds || []).map(String);
          const sharesAc = newHasAc && exHasAc && selectedAcIds.map(String).some((id) => exAc.includes(id));
          const sharesLight = newHasLight && exHasLight && selectedLightIds.map(String).some((id) => exLight.includes(id));
          if (!sharesAc && !sharesLight) return false; // disjoint devices → not a conflict
        }
      }
      if (!dayjs(b.date).isSame(date, "day")) return false;
      const existStart = parseBookingTime(b.date, b.startTime);
      const existEnd = parseBookingTime(b.date, b.endTime);
      // Overlap: new starts before existing ends AND new ends after existing starts
      return newStart.isBefore(existEnd) && newEnd.isAfter(existStart);
    });
  };

  // Helper: Get all blocked time ranges (in total minutes) for a given date + current floor
  // Blocked ranges = EXISTING BOOKINGS ONLY. The default-schedule window (and the
  // AC/lights-split AC window) is intentionally NOT blocked: with per-zone schedules and
  // AC/lights split the single fetched schedule is unreliable, so the picker allows any
  // time. Billing is unaffected — CRM applies free quota / bills only extended hours.
  const getBlockedRangesForDate = (date) => {
    const ranges = [];

    // Existing bookings on same floor + date
    if (date) {
      bookingsList.forEach(b => {
        if (b.cancelStatus) return;
        if (b.floorId !== floorValue?.value && b.floorName !== floorValue?.label) return;
        // Zone-scoped: only a same-zone booking conflicts (a floor-level booking with no zoneId
        // blocks any). On a zoned floor, until a zone is picked we can't know it → don't pre-block
        // any zone booking; the real same-zone overlap is enforced at submit (hasOverlappingBooking).
        if (zones.length > 0) {
          if (!selectedZoneId) return;
          if (b.zoneId && b.zoneId !== selectedZoneId) return;
        }
        // Device-class scoped (#3): don't block a slot for a booking of a different class.
        {
          const exHasAc = Array.isArray(b.selectedAcIds) && b.selectedAcIds.length > 0;
          const exHasLight = Array.isArray(b.selectedLightIds) && b.selectedLightIds.length > 0;
          const exBoth = !exHasAc && !exHasLight;
          const newHasAc = selectedAcIds.length > 0;
          const newHasLight = selectedLightIds.length > 0;
          // Device-scoped = every floor except explicit FLAT (unconfigured → device-scoped, matches billing).
          const deviceScoped = billingPrinciple !== "flat";
          if (deviceScoped) {
            // AC-only conflict for the picker. Lights are free/shareable (and default all-on),
            // so they must NOT drive a time-picker block. No AC chosen yet → nothing blocks;
            // once ACs are selected only a SHARED AC blocks. Full per-device rule (disjoint
            // lights + whole-floor) is enforced at submit.
            const exAc = (b.selectedAcIds || []).map(String);
            const sharesAc = newHasAc && exAc.length > 0 && selectedAcIds.map(String).some((id) => exAc.includes(id));
            if (!sharesAc) return;
          } else {
            // Flat floors price the whole scope → block existing windows sharing a device class.
            const newBoth = !newHasAc && !newHasLight;
            const sharesClass = newBoth || (newHasAc && (exHasAc || exBoth)) || (newHasLight && (exHasLight || exBoth));
            if (!sharesClass) return;
          }
        }
        if (!dayjs(b.date).isSame(date, "day")) return;
        const bStart = parseBookingTime(b.date, b.startTime);
        const bEnd = parseBookingTime(b.date, b.endTime);
        ranges.push({ startMin: bStart.hour() * 60 + bStart.minute(), endMin: bEnd.hour() * 60 + bEnd.minute() });
      });
    }

    return ranges;
  };

  // Devices already reserved by an overlapping booking for a given date+time window (+zone).
  // Feeds the DevicePicker so the user can't pick a unit that's already booked (the submit-time
  // hasOverlappingBooking check stays as the safety net). Mirrors its scope/zone/time rules; on
  // a FLAT floor (or against a whole-scope booking with no explicit device pick) the whole scope
  // is taken → block every device. AC ids are the acs._id (the DevicePicker's `ac.acId` field).
  const getBlockedDevicesForWindow = (date, startTime, endTime) => {
    const empty = { blockAll: false, acIds: new Set(), lightIds: new Set(), windowById: new Map() };
    if (!date || !startTime || !endTime) return empty;
    const newStart = parseBookingTime(date.format("YYYY-MM-DD"), startTime.format("HH:mm"));
    let newEnd = parseBookingTime(date.format("YYYY-MM-DD"), endTime.format("HH:mm"));
    if (endTime.hour() === 0 && endTime.minute() === 0) newEnd = newEnd.add(1, "day");
    if (!newStart.isValid() || !newEnd.isValid() || !newEnd.isAfter(newStart)) return empty;
    // On a zoned floor the zone is chosen before devices load; without it we can't scope.
    if (zones.length > 0 && !selectedZoneId) return empty;

    const deviceScoped = billingPrinciple !== "flat";
    const acIds = new Set();
    const lightIds = new Set();
    const windowById = new Map();
    let blockAll = false;

    bookingsList.forEach((b) => {
      if (b.cancelStatus) return;
      if (b.floorId !== floorValue?.value && b.floorName !== floorValue?.label) return;
      if (selectedZoneId && b.zoneId && b.zoneId !== selectedZoneId) return;
      if (!dayjs(b.date).isSame(date, "day")) return;
      const existStart = parseBookingTime(b.date, b.startTime);
      const existEnd = parseBookingTime(b.date, b.endTime);
      if (!(newStart.isBefore(existEnd) && newEnd.isAfter(existStart))) return; // no overlap

      const win = `${existStart.format("h:mm A")}–${existEnd.format("h:mm A")}`;
      const exHasAc = Array.isArray(b.selectedAcIds) && b.selectedAcIds.length > 0;
      const exHasLight = Array.isArray(b.selectedLightIds) && b.selectedLightIds.length > 0;
      const exWholeScope = !exHasAc && !exHasLight;
      if (!deviceScoped || exWholeScope) { blockAll = true; return; }
      (b.selectedAcIds || []).map(String).forEach((id) => { acIds.add(id); if (!windowById.has(id)) windowById.set(id, win); });
      (b.selectedLightIds || []).map(String).forEach((id) => { lightIds.add(id); if (!windowById.has(id)) windowById.set(id, win); });
    });
    return { blockAll, acIds, lightIds, windowById };
  };

  useEffect(() => {
    if (locationValue && floorValue) {
      fetchBookingsList();
      fetchTodaySchedule();
    } else {
      setBookingsList([]);
      setTodayScheduleStart(null);
      setTodayScheduleEnd(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationValue, floorValue]);

  // Cancel Booking State
  const [cancelBookingModal, setCancelBookingModal] = useState(false);
  const [cancelBookingData, setCancelBookingData] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleCancelClick = (booking) => {
    setCancelBookingData(booking);
    setCancelReason("");
    setCancelBookingModal(true);
  };

  // Fix #5: Cancel reason is optional for client (PRD: client just clicks Cancel)
  const handleConfirmCancel = async () => {
    try {
      setCancelLoading(true);
      const cancelPayload = {
        requestId: cancelBookingData?.requestId,
      };
      if (cancelReason.trim()) {
        cancelPayload.reasonCancel = cancelReason.trim();
      }
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/extended-hours/cancel`,
        cancelPayload
      );

      toast.success(response?.data?.message || "Booking cancelled successfully");
      setCancelBookingModal(false);
      setCancelBookingData(null);
      setCancelReason("");
      fetchBookingsList();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to cancel booking");
    } finally {
      setCancelLoading(false);
    }
  };

  // End Booking Now State — ends an ongoing booking at the current time
  const [endBookingData, setEndBookingData] = useState(null);
  const [endReason, setEndReason] = useState("");
  const [endLoading, setEndLoading] = useState(false);

  const closeEndBookingModal = () => {
    setEndBookingData(null);
    setEndReason("");
  };

  const handleConfirmEndBooking = async () => {
    try {
      setEndLoading(true);
      const reason = endReason.trim();
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_NEXUS_URL}/api/cspoc/extended-hours/${encodeURIComponent(endBookingData?.requestId)}/end`,
        reason ? { reason } : {}
      );
      toast.success(response?.data?.message || "Booking ended successfully");
      closeEndBookingModal();
      fetchBookingsList();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to end booking");
    } finally {
      setEndLoading(false);
    }
  };

  // Confirm Instant On Modal State
  const [confirmInstantOn, setConfirmInstantOn] = useState(false);
  const [instantOnLoading, setInstantOnLoading] = useState(false);
  const [instantOnResult, setInstantOnResult] = useState(null);
  // Snapshot of the chosen auto-stop time for the success modal — captured at submit
  // BEFORE resetBookingForm() clears the live `instantOffAtTime`, else the modal shows
  // an empty "Now – ()" / "auto-stop at .".
  const [instantOnEnd, setInstantOnEnd] = useState(null);

  const handleConfirmInstantOn = async () => {
    try {
      setInstantOnLoading(true);
      const authToken = JSON.parse(localStorage.getItem("headData"));

      const now = dayjs();
      const payload = {
        locationId: locationValue?.locationId,
        clientId: locationValue?.clientId,
        clientName: locationValue?.clientName,
        floorId: floorValue?.value,
        floorName: floorValue?.label,
        date: now.format("YYYY-MM-DD"),
        startTime: now.format("HH:mm"),
        endTime: instantOffAtTime
          ? (instantOffAtTime.hour() === 0 && instantOffAtTime.minute() === 0 ? "23:59:59" : `${instantOffAtTime.format("HH:mm")}:00`)
          : undefined,
        source: 'cspoc',
        // Single-zone booking — sent when the floor is zoned.
        ...(selectedZoneId ? { zoneId: selectedZoneId, zoneName: selectedZone?.zoneName } : {}),
        // Per-device booking — omitted when nothing is picked (turns on the whole zone/floor).
        ...(selectedAcIds.length ? { selectedAcIds } : {}),
        ...(selectedLightIds.length ? { selectedLightIds } : {}),
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/extended-hours`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authToken?.spintlyToken}`,
          },
        }
      );

      setInstantOnResult(response?.data?.data || response?.data);
      toast.success(response?.data?.message || "Instant ON activated successfully");
      setInstantOn(false);
      // Snapshot the auto-stop time for the success modal BEFORE the reset clears it.
      setInstantOnEnd(instantOffAtTime);
      setConfirmInstantOn(true);
      // Reset the whole form so the next open starts clean.
      resetBookingForm();
      fetchBookingsList();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to activate Instant ON");
    } finally {
      setInstantOnLoading(false);
    }
  };





   const sourceBookingOptions = [
    { value:"all", label: "All" },
    { value: "cspoc", label: "My Bookings" },
    { value: "ops", label: "Ops Booked" },
  ];






  // All Upcoming Bookings Modal State
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [upcomingBookingsMonth, setUpcomingBookingsMonth] = useState(dayjs());
  const [upcomingBookingsList, setUpcomingBookingsList] = useState([]);
  const [upcomingBookingsLoading, setUpcomingBookingsLoading] = useState(false);

  const fetchUpcomingBookings = async (month) => {
    try {
      const locationId = locationValue?.locationId;
      const clientId = locationValue?.clientId;
      if (!locationId) return;

      setUpcomingBookingsLoading(true);
      const selectedMonth = month || upcomingBookingsMonth;
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/extended-hours/location/${locationId}`,
        {
          params: {
            month: selectedMonth.month() + 1,
            year: selectedMonth.year(),
            page: 1,
            limit: 500,
            clientId,
          },
        }
      );

      const bookings = response?.data?.data || [];
      setUpcomingBookingsList(bookings);
    } catch (error) {
      console.error("Error fetching upcoming bookings:", error);
      setUpcomingBookingsList([]);
    } finally {
      setUpcomingBookingsLoading(false);
    }
  };

  const handleUpcomingBookingsOpen = () => {
    setShowAllUpcoming(true);
    setUpcomingBookingsMonth(dayjs());
    fetchUpcomingBookings(dayjs());
  };

  const filteredUpcomingBookings = upcomingBookingsList.filter((b) => {
    if (b.source !== "cspoc" && b.bookingReason !== "Client request") return false;
    if (b.cancelStatus) return false;
    const start = parseBookingTime(b.date, b.startTime);
    return start.isAfter(dayjs());
  });

  const upcomingBookingsColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: "Floor",
      dataIndex: "floorName",
      key: "floorName",
      render: (text, record) => (
        <div>
          <span className="fw-600">{text || "—"}</span>
          {record.source && record.source !== "cspoc" && (
            <Tag color="purple" style={{ marginLeft: 6, fontSize: "10px" }}>Ops Booked</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Zone",
      dataIndex: "zoneName",
      key: "zoneName",
      render: (text) =>
        text ? (
          <span className="fw-600">{text}</span>
        ) : (
          <span className="SwTextGray">Whole floor</span>
        ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      render: (text) => (
        <span className="fw-600">{text ? dayjs(text).format("MMM DD, YYYY") : "—"}</span>
      ),
    },
    {
      title: "Time",
      key: "time",
      render: (_, record) => (
        <span>
          {formatBookingTime(record.startTime)} – {formatBookingTime(record.endTime)}
        </span>
      ),
    },
    {
      title: "Request ID",
      dataIndex: "requestId",
      key: "requestId",
      render: (text) => (
        <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{text || "—"}</span>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (text) => {
        if (text === "cspoc") {
          return <Tag color="blue">CSPOC</Tag>;
        }
        return <Tag color="purple">{text || "Ops"}</Tag>;
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        if (record.cancelStatus) {
          return <Tag color="error">Cancelled</Tag>;
        }
        return <Tag color="success">Upcoming</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => {
        if (record.cancelStatus) return "—";
        return (
          <SwButton
            variant="dark-outline"
            size="sm"
            disabled={!isCancellable(record)}
            onClick={() => handleCancelClick(record)}
          >
            Cancel
          </SwButton>
        );
      },
    },
  ];

  // Past Bookings State
  const [showPastBookings, setShowPastBookings] = useState(false);
  const [pastBookingsList, setPastBookingsList] = useState([]);
  const [pastBookingsLoading, setPastBookingsLoading] = useState(false);
  const [pastBookingsMonth, setPastBookingsMonth] = useState(dayjs());
  const [pastBookingsSearch, setPastBookingsSearch] = useState("");
  const [pastBookingsSource, setPastBookingsSource] = useState("all");
  const [statementBooking, setStatementBooking] = useState(null);

  const fetchPastBookings = async (month) => {
    try {
      const locationId = locationValue?.locationId;
      const clientId = locationValue?.clientId;
      if (!locationId) return;

      setPastBookingsLoading(true);
      const selectedMonth = month || pastBookingsMonth;
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_NEXUS_URL}/api/ops/properties/extended-hours/location/${locationId}`,
        {
          params: {
            month: selectedMonth.month() + 1,
            year: selectedMonth.year(),
            page: 1,
            limit: 500,
            status: 'completed',
            clientId,
          },
        }
      );

      const pastData = response?.data?.data || [];
      console.log("📋 Past Bookings API Response:", JSON.stringify(pastData, null, 2));
      setPastBookingsList(pastData);
    } catch (error) {
      console.error("Error fetching past bookings:", error);
      setPastBookingsList([]);
    } finally {
      setPastBookingsLoading(false);
    }
  };

  const handlePastBookingsOpen = () => {
    setShowPastBookings(true);
    setPastBookingsSearch("");
    setPastBookingsSource("all");
    fetchPastBookings();
  };

  const filteredPastBookings = pastBookingsList.filter((b) => {
    // Show all CSPOC bookings; for Ops, only show "Client request"
    if (b.source !== "cspoc" && b.bookingReason !== "Client request") return false;

    // Only show bookings where end time has passed (past bookings only)
    const endDateTime = parseBookingTime(b.date, b.endTime);
    if (endDateTime.isAfter(dayjs())) return false;

    // Source filter
    if (pastBookingsSource === "cspoc" && b.source !== "cspoc") return false;
    if (pastBookingsSource === "ops" && b.source === "cspoc") return false;

    // Search filter by request ID
    if (pastBookingsSearch) {
      return b.requestId?.toLowerCase().includes(pastBookingsSearch.toLowerCase());
    }
    return true;
  });

  const pastBookingsColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: "Floor",
      dataIndex: "floorName",
      key: "floorName",
      render: (text, record) => (
        <div>
          <span className="fw-600">{text || "—"}</span>
          {record.source && record.source !== "cspoc" && (
            <Tag color="purple" style={{ marginLeft: 6, fontSize: "10px" }}>Ops Booked</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Zone",
      dataIndex: "zoneName",
      key: "zoneName",
      render: (text) =>
        text ? (
          <span className="fw-600">{text}</span>
        ) : (
          <span className="SwTextGray">Whole floor</span>
        ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      minWidth:120,
      noWrap: true,
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      render: (text) => (
        <span className="fw-600">{text ? dayjs(text).format("MMM DD, YYYY") : "—"}</span>
      ),
    },
    {
      title: "Time",
      key: "time",
      render: (_, record) => (
        <span>
          {formatBookingTime(record.startTime)} – {formatBookingTime(record.endTime)}
        </span>
      ),
    },
    {
      title: "Billable Hours",
      key: "billableHours",
      minWidth:140,
      noWrap: true,
      sorter: (a, b) => {
        const hrsA = a.billableHours || Math.ceil(dayjs(a.endTime, "HH:mm").diff(dayjs(a.startTime, "HH:mm"), "hour", true));
        const hrsB = b.billableHours || Math.ceil(dayjs(b.endTime, "HH:mm").diff(dayjs(b.startTime, "HH:mm"), "hour", true));
        return hrsA - hrsB;
      },
      render: (_, record) => {
        const hours = record.billableHours ||
          Math.max(Math.ceil(dayjs(record.endTime, "HH:mm").diff(dayjs(record.startTime, "HH:mm"), "hour", true)), 1);
        return <span>{hours} {hours === 1 ? "hour" : "hours"}</span>;
      },
    },
    {
      title: "Amount",
      key: "amount",
      sorter: (a, b) => (a.grossEstimate ?? a.totalAmount ?? a.amount ?? 0) - (b.grossEstimate ?? b.totalAmount ?? b.amount ?? 0),
      render: (_, record) => {
        // API returns the priced amount as `grossEstimate` (not totalAmount/amount).
        const amt = record.grossEstimate ?? record.totalAmount ?? record.amount;
        return amt != null
          ? <span className="fw-600">₹{Number(amt).toLocaleString("en-IN")}</span>
          : <span className="text-muted">—</span>;
      },
    },
    {
      title: "Request ID",
      dataIndex: "requestId",
      key: "requestId",
      render: (text) => (
        <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{text || "—"}</span>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (text) => {
        if (text === "cspoc") {
          return <Tag color="blue">CSPOC</Tag>;
        }
        return <Tag color="purple">{text || "Ops"}</Tag>;
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        if (record.cancelStatus) {
          return <Tag color="error">Cancelled</Tag>;
        }
        if (record.status === "ENDED_EARLY") {
          return (
            <>
              <Tag color="warning">Ended early</Tag>
              {record.scheduledEndTime && (
                <div style={{ fontSize: "11px", color: "#64748b" }}>booked till {formatBookingTime(record.scheduledEndTime)}</div>
              )}
            </>
          );
        }
        return <Tag color="success">Completed</Tag>;
      },
    },
    // {
    //   title: "Action",
    //   key: "action",
    //   width: 130,
    //   render: (_, record) => (
    //     <SwButton
    //       variant="dark-outline"
    //       size="sm"
    //       onClick={() => setStatementBooking(record)}
    //     >
    //       View Statement
    //     </SwButton>
    //   ),
    // },
  ];

  return (
    <>
      <div className="Dashboard_Card LightGridCard">
        <div className="Dashboard_Card_Header Lightgrid_Card_Header">
          <div className="LightGrid_Header_Left">
            <h2>Extended Hours</h2>
          </div>
          <div className="LightGrid_LocationSelector">
            <div className="LightGrid_Location_">
              <span className="me-1">
                <i class="bi bi-geo-alt-fill" />
              </span>
              <SwSelect
                options={locationOptions}
                value={locationValue}
                onChange={handleLocationChange}
                placeholder="Select Location"
                isClearable={false}
              />
            </div>
            <div>
              <SwSelect
                options={floorOptions}
                value={floorValue}
                onChange={setFloorValue}
                placeholder="Select Floor"
                isClearable={false}
                className="LCS_Location_Floor_Select"
              />
            </div>
          </div>
        </div>

        <div className="Dashboard_Card_Body" style={{ minHeight: '200px' }}>
          <div className="ExtendedBody">
            <div className="ExtendedActions">
              <div className="ExtendedActions_Left">
                <SwButton
                  variant="primary"
                  size="sm"
                  icon={<i class="bi bi-calendar2-check-fill" />}
                  disabled={!locationValue || !floorValue}
                  onClick={() => setScheduleBooking(true)}
                >
                  Schedule Booking
                </SwButton>
              </div>
              <div className="ExtendedActions_Right d-flex gap-2">
                <SwButton
                  variant="dark"
                  size="sm"
                  disabled={!locationValue || !floorValue || isInstantOnDisabled()}
                  onClick={() => setInstantOn(true)}
                  icon={<i class="bi bi-lightning-charge" />}
                  title={isInstantOnDisabled() ? getInstantOnDisabledReason() : ""}
                >
                  Instant ON
                </SwButton>
                <SwButton
                  variant="dark-outline"
                  size="sm"
                  disabled={!locationValue || !floorValue}
                  onClick={handlePastBookingsOpen}
                  icon={<i className="bi bi-clock-history" />}
                >
                  Past Bookings
                </SwButton>
                <SwIconButton
                  size="sm"
                  variant="default"
                  disabled={!locationValue}
                  onClick={handleHolidaysListOpen}
                  tooltip="Holidays List"
                >
                  <i className="bi bi-calendar-week" />
                </SwIconButton>
              </div>
            </div>
            {locationValue && floorValue && isInstantOnDisabled() && (
              <SwAlert
                variant="info"
                icon={<i className="bi bi-info-circle-fill" />}
                className="mb-2"
              >
                {getInstantOnDisabledReason()}
              </SwAlert>
            )}
            {/* Show bookings or no-data based on location + floor selection */}
            {(!locationValue || !floorValue) ? (
              <SwNoData message="Please select Location and Floor first" />
            ) : (
            <>
            {/* Extended Active (Fix #2: support multiple active bookings) */}
            {getActiveBookings().length > 0 && (
            <div className="Extended_Card_container Extended_Active">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="SwSubHeading Hv2">
                  <h6 className="d-flex align-items-center mb-0">
                    <span>Active Now</span>
                    <span className="ActiveDot ms-1" />
                  </h6>
                </div>
                <span style={{ fontSize: "12px", color: "#00a308", fontWeight: 600 }}>{getActiveBookings().length} Floor{getActiveBookings().length > 1 ? 's' : ''}</span>
              </div>
              {getActiveBookings().map((activeBooking) => (
              <div className={`ExtendedBookingCard${activeBooking.source && activeBooking.source !== 'cspoc' ? ' OpsBookingCard' : ''}`} key={activeBooking.requestId} style={{ marginBottom: 10 }}>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-600" style={{ fontSize: "14px" }}>{activeBooking.floorName}</span>
                    {activeBooking.zoneName && (
                      <span className="EWH-ZoneChip">{activeBooking.zoneName}</span>
                    )}
                    {activeBooking.source && activeBooking.source !== 'cspoc' && (
                      <span className="OpsBookingTag">Ops Booked</span>
                    )}
                  </div>
                  <span className="RemainingHrs">{getRemainingTime(activeBooking)}</span>
                </div>
                <p style={{ fontSize: "12px", color: "#64748b", marginBottom: 4 }}>
                  Today, {formatBookingTime(activeBooking.startTime)} - {formatBookingTime(activeBooking.endTime)}
                </p>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="EWH" style={{ display: "inline-block" }}>{activeBooking.requestId}</div>
                  {activeBooking.canEndNow && (
                    <SwButton variant="dark-outline" size="sm" onClick={() => setEndBookingData(activeBooking)} style={{ flexShrink: 0, marginLeft: 12 }}>
                      End Now
                    </SwButton>
                  )}
                </div>
              </div>
              ))}
            </div>
            )}

            {/* Extended Upcoming (Fix #3: Ops visual distinction) */}
            {getUpcomingBookings().length > 0 && (
            <div className="Extended_Card_container Extended_Upcoming">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="SwSubHeading Hv2">
                  <h4 className="d-flex align-items-center mb-0">
                    <span>Upcoming Bookings</span>
                  </h4>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="BookingTag">{getUpcomingBookings().length} Booking{getUpcomingBookings().length > 1 ? 's' : ''}</div>
                  {getUpcomingBookings().length > 3 && (
                    <SwButton variant="dark-outline" size="sm" onClick={handleUpcomingBookingsOpen}>
                      View All
                    </SwButton>
                  )}
                </div>
              </div>

              {getUpcomingBookings().slice(0, 3).map((booking) => (
              <div className={`ExtendedBookingCard${booking.source && booking.source !== 'cspoc' ? ' OpsBookingCard' : ''}`} key={booking.requestId || booking.id}>
                <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: 8 }}>
                  <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                    <span className="fw-600" style={{ fontSize: "14px" }}>{booking.floorName || dayjs(booking.date).format("MMMM DD, YYYY")}</span>
                    {booking.zoneName && (
                      <span className="EWH-ZoneChip">{booking.zoneName}</span>
                    )}
                    {booking.source && booking.source !== 'cspoc' && (
                      <span className="OpsBookingTag">Ops Booked</span>
                    )}
                    {booking.cancelStatus && (
                      <Tag color="error">Cancelled</Tag>
                    )}
                  </div>
                  {!booking.cancelStatus && (
                  <SwButton variant="dark-outline" size="sm" disabled={!isCancellable(booking)} onClick={() => handleCancelClick(booking)} style={{ flexShrink: 0, marginLeft: 12 }}>
                    Cancel
                  </SwButton>
                  )}
                </div>
                <p style={{ fontSize: "12px", color: "#64748b", marginBottom: 4 }}>
                  {dayjs(booking.date).format("MMM DD, YYYY")} · {formatBookingTime(booking.startTime)} - {formatBookingTime(booking.endTime)}
                </p>
                {booking.source && booking.source !== 'cspoc' && booking.reason && (
                  <p className="OpsBookingNote" style={{ marginBottom: 6 }}>Booked by Operations — {booking.reason}</p>
                )}
                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 6 }}>
                  <div className="EWH">{booking.requestId}</div>
                  <div className="RemainingHrs" />
                </div>

                {!isCancellable(booking) && (
                <SwAlert
                  className="mt-2"
                  variant="warning"
                  icon={<i className="bi bi-exclamation-triangle-fill" />}
                >
                  Cancellation unavailable — booking starts within 24 hours
                </SwAlert>
                )}
              </div>
              ))}

            </div>
            )}

            {/* No active or upcoming bookings */}
            {getActiveBookings().length === 0 && getUpcomingBookings().length === 0 && (
              <SwNoData message="No active or upcoming bookings" />
            )}
            </>
            )}
          </div>
        </div>
      </div>

      {/* Holidays List Modal */}
      <SwModal
        show={holidaysModalOpen}
        title={
          <span className="d-flex align-items-center gap-2">
            <i className="bi bi-calendar-week" />
            Holidays List{locationValue?.label ? ` — ${locationValue.label}` : ''}
          </span>
        }
        className="sw-default-modal"
        onClose={() => setHolidaysModalOpen(false)}
        centered
        size="md"
        footer={
          <div className="d-flex justify-content-end">
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={() => setHolidaysModalOpen(false)}
            >
              Close
            </SwButton>
          </div>
        }
      >
        <AntDTable
          columns={holidaysColumns}
          dataSource={holidaysList}
          loading={holidaysLoading}
          rowKey={(record, index) => record?.id || record?._id || record?.date || index}
          pagination={holidaysList.length > 10}
          pageSize={10}
          bordered
          size="small"
        />
      </SwModal>

      {/* Schedule Booking Modal */}
      <SwModal
        show={scheduleBooking}
        title="Schedule Extended Work Hours"
        className="sw-default-modal Mdl_1"
        onClose={() => { setScheduleBooking(false); resetBookingForm(); }}
        backdrop="static"
        centered
        keyboard={false}
        footer={
          <div className="d-flex justify-content-end align-items-center gap-3">
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={() => { setScheduleBooking(false); resetBookingForm(); }}
            >
              Cancel
            </SwButton>
            <SwButton
              variant="primary"
              size="sm"
             onClick={() => {
  if (!scheduleDate || !scheduleStartTime || !scheduleEndTime) {
    toast.error("Please select date and time");
    return;
  }

  // Validate: selected date cannot be before today
  if (scheduleDate.startOf("day").isBefore(dayjs().startOf("day"))) {
    toast.error("Selected date cannot be in the past");
    return;
  }

  // Validate: end time must be after start time (12:00 AM always valid as end-of-day)
  const endIsMidnight = scheduleEndTime.hour() === 0 && scheduleEndTime.minute() === 0;
  const startIsMidnight = scheduleStartTime.hour() === 0 && scheduleStartTime.minute() === 0;
  const bothMidnight = startIsMidnight && endIsMidnight;
  // Allow: end=12AM (treat as 23:59), or both=12AM (full day)
  if (!endIsMidnight && !bothMidnight && !scheduleEndTime.isAfter(scheduleStartTime)) {
    toast.error("End time must be after Start time");
    return;
  }

  // Validate: end time must not be in the past
  // 12:00 AM end means end-of-day (23:59), so check against next day
  if (endIsMidnight) {
    if (isPastDateTime(scheduleDate.add(1, "day"), scheduleEndTime)) {
      toast.error("End time cannot be in the past");
      return;
    }
  } else {
    if (isPastDateTime(scheduleDate, scheduleEndTime)) {
      toast.error("End time cannot be in the past");
      return;
    }
  }

// NOTE: booking time is intentionally NOT validated against the default schedule —
// with AC/lights-split and per-zone schedules that window is unreliable, so any time is
// allowed. Existing-booking overlap is still enforced separately.

  // Fix #8: Check for overlapping bookings
  const overlap = hasOverlappingBooking(scheduleDate, scheduleStartTime, scheduleEndTime, floorValue?.value);
  if (overlap) {
    toast.error(`Booking overlaps with existing booking ${overlap.requestId || ''} on this floor`);
    return;
  }

  // Single-zone: a zoned floor must be booked one zone at a time.
  if (zones.length > 0 && !selectedZoneId) {
    toast.error("Please select a zone for the booking");
    return;
  }

  // (#4) A booking must actuate something — at least one AC or one light.
  if (selectedAcIds.length === 0 && selectedLightIds.length === 0) {
    toast.error("Select at least one AC or light to book — an empty booking is not allowed.");
    return;
  }

  setConfirmScheduleBooking(true);
}}
            >
              Schedule
            </SwButton>
          </div>
        }
      >
        {zones.length > 0 && (
          <div className="SwFormGroup mb-3">
            <label className="SwFormLabel">Select Zone</label>
            <ZoneCardSelector
              zones={zones}
              selectedZoneId={selectedZoneId}
              onSelect={setSelectedZoneId}
              zoneSchedules={zoneSchedules}
            />
            {!selectedZoneId && (
              <span className="SwTextSmall SwTextGray">Select a zone to load its devices.</span>
            )}
          </div>
        )}

        <h3 className="SwModalSubHeading">Select Date & Time</h3>

        {/* On a zoned floor the schedule is per-zone, so the floor default is misleading
            until a zone is picked — hide the banner until then (the zone picker above
            already prompts "Select a zone"). */}
        {zones.length > 0 && !selectedZoneId ? null : scheduleDate && noDefaultSchedule ? (
          <SwAlert variant="warning" icon={<i className="bi bi-exclamation-triangle-fill" />}>
            There is no default schedule for the client on selected date
          </SwAlert>
        ) : (
          <SwAlert variant="info" icon={<i className="bi bi-info-circle-fill" />}>
            Default schedule
            {/* Zoned floor: show the SELECTED zone's own schedule (per-zone, already 12-hour) so
                the banner tracks the zone picked above. Whole-office floor: show the floor's
                default (lights, plus AC hours for ac-based), formatted to 12-hour. Rate removed. */}
            {selectedZoneId && zoneSchedules[selectedZoneId]
              ? ` (${zoneSchedules[selectedZoneId]}).`
              : acDefaultSchedule
                ? ` (Lights ${to12(defaultStartTime)} – ${to12(defaultEndTime)}${
                    billingPrinciple === "flat"
                      ? ""
                      : ` · AC ${acDefaultSchedule.is24x7 ? "24/7" : `${to12(acDefaultSchedule.startTime)} – ${to12(acDefaultSchedule.endTime)}`}`
                  }).`
                : ` (${to12(defaultStartTime)} – ${to12(defaultEndTime)}).`}
            {" Only hours beyond the default schedule are billed."}
          </SwAlert>
        )}

        <div className="ScheduleFields mt-3">
          <div className="row">
            <div className="col-md-6">
              <div className="SwFormGroup">
                <label className="SwFormLabel">Select Date</label>
                <AntDDatePicker
                  placeholder="Date"
                  // Render the calendar INSIDE the modal — react-bootstrap's focus trap +
                  // static backdrop otherwise block/hide the default body-portal popup,
                  // making dates unselectable.
                  getPopupContainer={(trigger) => trigger.parentElement}
                  value={scheduleDate}
                  disabledDate={(current) => current && current.startOf("day").isBefore(dayjs().startOf("day"))}
                  onChange={(date) => {
                    setScheduleDate(date);
                    setScheduleStartTime(null);
                    setScheduleEndTime(null);
                    setNoDefaultSchedule(false);
                    setDefaultStartTime(null);
                    setDefaultEndTime(null);

                    if (date && locationValue && floorValue) {
                      fetchClientSchedule(date);
                    }
                  }}
                  format="DD/MM/YYYY"
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="SwFormGroup">
                <label className="SwFormLabel">Start Time</label>
             <AntDTimePicker
  value={scheduleStartTime}
  getPopupContainer={(trigger) => trigger.parentElement}
  onChange={handleScheduleStartTimeChange}
  format="h:mm a"
  use12Hours
  placeholder="--:--"
  minuteStep={1}
  allowClear
  showNow
disabledTime={() => {
  if (!scheduleDate) return {};

  const blockedRanges = getBlockedRangesForDate(scheduleDate);
  const isToday = scheduleDate.isSame(dayjs(), "day");
  const now = dayjs();
  const currentTotalMin = isToday ? now.hour() * 60 + now.minute() : -1;

  const isMinBlocked = (totalMin) => {
    if (isToday && totalMin < currentTotalMin) return true;
    return blockedRanges.some(r => totalMin >= r.startMin && totalMin < r.endMin);
  };

  return {
    disabledHours: () => {
      const disabled = [];
      for (let h = 0; h < 24; h++) {
        if (h === 12) {
          // Never disable noon — AntD needs it enabled to fire onChange on PM toggle
        } else if (Array.from({ length: 60 }, (_, m) => m).every(m => isMinBlocked(h * 60 + m))) {
          disabled.push(h);
        }
      }
      return disabled;
    },
    disabledMinutes: (selectedHour) => {
      const disabled = [];
      for (let m = 0; m < 60; m++) {
        if (isMinBlocked(selectedHour * 60 + m)) disabled.push(m);
      }
      return disabled;
    },
  };
}}
/>
              </div>
            </div>
            <div className="col-md-3">
              <div className="SwFormGroup">
                <label className="SwFormLabel">End Time</label>
             <AntDTimePicker
  value={scheduleEndTime}
  getPopupContainer={(trigger) => trigger.parentElement}
  onChange={handleScheduleEndTimeChange}
  format="h:mm a"
  use12Hours
  placeholder="--:--"
  minuteStep={1}
  allowClear
  showNow
disabledTime={() => {
  if (!scheduleStartTime) return {};

  const blockedRanges = getBlockedRangesForDate(scheduleDate);
  const startTotalMin = scheduleStartTime.hour() * 60 + scheduleStartTime.minute();


  // Find the earliest blocked range start AFTER our start time
  let maxEndMin = 24 * 60; // default: end of day
  blockedRanges.forEach(r => {
    if (r.startMin > startTotalMin && r.startMin < maxEndMin) {
      maxEndMin = r.startMin;
    }
  });

  const minEndMin = startTotalMin + 1; // at least 1 min after start

  const isMinDisabled = (totalMin) => {
    if (totalMin < minEndMin) return true;
    if (totalMin > maxEndMin) return true;
    return false;
  };

  return {
    disabledHours: () => {
      const disabled = [];
      for (let h = 0; h < 24; h++) {
        if (h === 0) {
          // 12:00 AM always enabled as end-of-day — never disable
        } else if (h === 12) {
          // Never disable noon — AntD needs it enabled to fire onChange on PM toggle
        } else if (Array.from({ length: 60 }, (_, m) => m).every(m => isMinDisabled(h * 60 + m))) {
          disabled.push(h);
        }
      }
      return disabled;
    },
    disabledMinutes: (selectedHour) => {
      // 12 AM (hour 0) is the end-of-day marker (=24:00) for a booking that started LATER
      // in the day — only 12:00 AM is valid then. But when the booking itself STARTS in the
      // 12 AM hour (early morning, e.g. 12:00–12:30 AM), 12:xx AM is a normal same-day end
      // and follows the usual after-start bounds, so let the minutes through.
      if (selectedHour === 0 && scheduleStartTime.hour() !== 0) {
        return Array.from({ length: 59 }, (_, i) => i + 1); // only 12:00 AM valid
      }
      return Array.from({ length: 60 }, (_, m) => m).filter(m => isMinDisabled(selectedHour * 60 + m));
    },
  };
}}
/>
              </div>
            </div>
          </div>
        </div>

        {/* Per-device booking — pick exact ACs (chargeable) + lights (free) */}
        {(() => {
          const blk = getBlockedDevicesForWindow(scheduleDate, scheduleStartTime, scheduleEndTime);
          return (
            <DevicePicker
              acs={bookableAcs}
              lights={bookableLights}
              selectedAcIds={selectedAcIds}
              selectedLightIds={selectedLightIds}
              onChangeAcIds={setSelectedAcIds}
              onChangeLightIds={setSelectedLightIds}
              billableHours={billableHoursBetween(scheduleStartTime, scheduleEndTime)}
              loading={devicesLoading}
              billingPrinciple={billingPrinciple}
              flatRateForDay={flatRateFor(scheduleDate)}
              blockedAcIds={blk.acIds}
              blockedLightIds={blk.lightIds}
              blockAll={blk.blockAll}
              blockedWindowById={blk.windowById}
            />
          );
        })()}
      </SwModal>

      {/* Confirm Schedule Booking Modal */}
      <SwModal
        show={confirmScheduleBooking}
        className="sw-small-modal ExpandedWidth Mdl_2"
        title="Confirm Schedule Booking"
        onClose={() => { setConfirmScheduleBooking(false); setConfirmLoading(false); }}
        backdrop="static"
        centered
        keyboard={false}
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3">
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={() => { setConfirmScheduleBooking(false); setConfirmLoading(false); }}
            >
              Back
            </SwButton>
           <SwButton
              variant="primary"
              size="sm"
              onClick={handleConfirmSchedule}
              loading={confirmLoading}
            >
              Confirm Booking
            </SwButton>
          </div>
        }
      >
        <div className="">
          {/* Date and Time Data for confirmation*/}
          <div className="ConfirmBookingTable">
            <table class="table table-bordered">
              <tbody>
                <tr>
                  <td>Floor</td>
                  <td>
               <span className="fw-600">{floorValue?.label}</span>
                  </td>
                </tr>
                <tr>
                  <td>Date</td>
                  <td>
                    <span className="fw-600">
  {scheduleDate?.format("MMM DD, YYYY")}
</span>
                  </td>
                </tr>
                <tr>
                  <td>Time</td>
                  <td>
                  <span className="fw-600">
  {scheduleStartTime?.format("h:mm A")} – {scheduleEndTime?.format("h:mm A")}
  {scheduleStartTime && scheduleEndTime && (() => {
    const isMidnight = scheduleEndTime.hour() === 0 && scheduleEndTime.minute() === 0;
    const startTotalSec = scheduleStartTime.hour() * 3600 + scheduleStartTime.minute() * 60;
    // 12:00 AM treated as 23:59:59 — ceil seconds to nearest minute
    const totalMin = isMidnight
      ? Math.ceil((23 * 3600 + 59 * 60 + 59 - startTotalSec) / 60)
      : scheduleEndTime.diff(scheduleStartTime, "minute");
    const hrs = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    if (mins === 0) return ` (${hrs} hour${hrs > 1 ? 's' : ''})`;
    if (hrs === 0) return ` (${mins} min)`;
    return ` (${hrs}h ${mins}m)`;
  })()}
</span>
                  </td>
                </tr>
                <tr>
                  <td>Billable Hours</td>
                  <td>
                    <span className="fw-600">
                      {(() => {
                        if (!scheduleStartTime || !scheduleEndTime) return "—";
                        const endIsMidnight = scheduleEndTime.hour() === 0 && scheduleEndTime.minute() === 0;
                        const startTotalSec = scheduleStartTime.hour() * 3600 + scheduleStartTime.minute() * 60;
                        // 12:00 AM treated as 23:59:59 — ceil seconds to nearest minute
                        const totalMin = endIsMidnight
                          ? Math.ceil((23 * 3600 + 59 * 60 + 59 - startTotalSec) / 60)
                          : scheduleEndTime.diff(scheduleStartTime, "minute");
                        const billable = Math.ceil(totalMin / 60);
                        const hours = Math.max(billable, 1);
                        return `${hours} hour${hours > 1 ? 's' : ''} (beyond default schedule, minimum 1 hour billing)`;
                      })()}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="my-3">
            <SwAlert
              variant="info"
              icon={<i className="bi bi-info-circle-fill" />}
            >
              You can cancel any booking (including Ops-initiated) up to 24
              hours before start time. A confirmation email will be sent.
              Statement generated after utilization will not be adjusted.
            </SwAlert>
          </div>
        </div>
      </SwModal>

      {/* Instant On Modal */}
      <SwModal
        show={instantOn}
        title="Instant On"
        className="sw-default-modal Mdl_1"
        centered
        keyboard={false}
        onClose={() => { setInstantOn(false); resetBookingForm(); }}
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3">
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={() => { setInstantOn(false); resetBookingForm(); }}
            >
              Back
            </SwButton>
            <SwButton
              variant="primary"
              size="sm"
              onClick={() => {
                if (!instantOffAtTime) {
                  toast.error("Please select an end time");
                  return;
                }

                const now = dayjs();
                const isMidnightEnd = instantOffAtTime.hour() === 0 && instantOffAtTime.minute() === 0;
                const nowMin = now.hour() * 60 + now.minute();
                const selMin = instantOffAtTime.hour() * 60 + instantOffAtTime.minute();
                // 12:00 AM always valid as end-of-day; compare time-of-day only
                if (!isMidnightEnd && selMin <= nowMin) {
                  toast.error("End time must be after the current time");
                  return;
                }

                // Single-zone: a zoned floor must be booked one zone at a time.
                if (zones.length > 0 && !selectedZoneId) {
                  toast.error("Please select a zone");
                  return;
                }

                // (#4) A booking must actuate something — at least one AC or one light.
                if (selectedAcIds.length === 0 && selectedLightIds.length === 0) {
                  toast.error("Select at least one AC or light — an empty booking is not allowed.");
                  return;
                }

                handleConfirmInstantOn();
              }}
              loading={instantOnLoading}
              icon={<i className="bi bi-lightning-charge" />}
            >
              Turn On Now
            </SwButton>
          </div>
        }
      >
        <div className="">
          <h3 className="SwModalSubHeading">
            Start service now — select floor and end time
          </h3>

          <div className="InstantOnFields">
            <div className="row">
              <div className="col-md-6">
                <div className="SwFormGroup">
                  <label className="SwFormLabel">Floor</label>
                  <SwSelect
                    options={floorOptions.map((f) => {
                      const isActive = getActiveBookings().some(
                        (b) => b.floorId === f.value || b.floorName === f.label
                      );
                      return {
                        ...f,
                        label: isActive ? `${f.label} (service already active)` : f.label,
                        isDisabled: isActive,
                      };
                    })}
                    value={floorValue}
                    onChange={setFloorValue}
                    placeholder="Select Floor"
                    isClearable={false}
                    isDisabled={!locationValue}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="SwFormGroup">
                  <label className="SwFormLabel">Turn off at</label>
                  <AntDTimePicker
                    value={instantOffAtTime}
                    // Render the time panel INSIDE the modal — the SwModal focus trap +
                    // static backdrop otherwise block the default body-portal popup,
                    // making the end time unselectable.
                    getPopupContainer={(trigger) => trigger.parentElement}
                    onChange={handleInstantOffAtTimeChange}
                    format="h:mm a"
                    use12Hours
                    placeholder="--:--"
                    minuteStep={1}
                    allowClear
                    showNow
                    disabledTime={() => {
                      const now = dayjs();
                      const nowTotalMin = now.hour() * 60 + now.minute();

                      // A slot is disabled if it is at or before current time.
                      // Exception: 12:00 AM (totalMin=0) is always enabled as end-of-day.
                      const isDisabled = (totalMin) => {
                        if (totalMin === 0) return false;
                        return totalMin <= nowTotalMin;
                      };

                      return {
                        disabledHours: () => {
                          const disabled = [];
                          for (let h = 0; h < 24; h++) {
                            if (h === 0) continue; // 12:00 AM always enabled
                            if (h === 12) continue; // noon never disabled — needed for AM/PM toggle
                            if (Array.from({ length: 60 }, (_, m) => m).every(m => isDisabled(h * 60 + m))) {
                              disabled.push(h);
                            }
                          }
                          return disabled;
                        },
                        disabledMinutes: (selectedHour) => {
                          if (selectedHour === 0) {
                            // 12:00 AM (:00) always enabled; disable other minutes if past
                            return Array.from({ length: 59 }, (_, i) => i + 1).filter(m => isDisabled(m));
                          }
                          return Array.from({ length: 60 }, (_, m) => m).filter(m => isDisabled(selectedHour * 60 + m));
                        },
                      };
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {zones.length > 0 && (
            <div className="SwFormGroup mt-3">
              <label className="SwFormLabel">Select Zone</label>
              <ZoneCardSelector
                zones={zones}
                selectedZoneId={selectedZoneId}
                onSelect={setSelectedZoneId}
                zoneSchedules={zoneSchedules}
              />
              {!selectedZoneId && (
                <span className="SwTextSmall SwTextGray">Select a zone to load its devices.</span>
              )}
            </div>
          )}

          {/* Per-device booking — pick exact ACs (chargeable) + lights (free) */}
          {(() => {
            const now = dayjs();
            const blk = getBlockedDevicesForWindow(now, now, instantOffAtTime);
            return (
              <DevicePicker
                acs={bookableAcs}
                lights={bookableLights}
                selectedAcIds={selectedAcIds}
                selectedLightIds={selectedLightIds}
                onChangeAcIds={setSelectedAcIds}
                onChangeLightIds={setSelectedLightIds}
                billableHours={billableHoursBetween(dayjs(), instantOffAtTime)}
                loading={devicesLoading}
                billingPrinciple={billingPrinciple}
                flatRateForDay={flatRateFor(dayjs())}
                blockedAcIds={blk.acIds}
                blockedLightIds={blk.lightIds}
                blockAll={blk.blockAll}
                blockedWindowById={blk.windowById}
              />
            );
          })()}

          <div className="my-3">
            <SwAlert
              variant="warning"
              icon={<i className="bi bi-info-circle-fill" />}
            >
              Minimum 1 hour billing. Service will auto-stop at your selected
              time. You will be billed for the full selected duration.
            </SwAlert>
          </div>
        </div>
      </SwModal>

      {/* End Booking Now Modal */}
      <SwModal
        show={!!endBookingData}
        title="End Booking Now"
        className="sw-small-modal ExpandedWidth Mdl_2"
        centered
        keyboard={false}
        onClose={() => { if (!endLoading) closeEndBookingModal(); }}
        backdrop="static"
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3">
            <SwButton variant="dark-outline" size="sm" onClick={closeEndBookingModal} disabled={endLoading}>
              Keep Running
            </SwButton>
            <SwButton variant="danger" size="sm" onClick={handleConfirmEndBooking} loading={endLoading}>
              End Booking Now
            </SwButton>
          </div>
        }
      >
        <div className="">
          <div className="ConfirmBookingTable">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <td>Floor</td>
                  <td><span className="fw-600">{endBookingData?.floorName}</span></td>
                </tr>
                <tr>
                  <td>Booked</td>
                  <td>
                    <span className="fw-600">
                      {endBookingData ? `${dayjs(endBookingData.date).format("MMM DD, YYYY")}, ${formatBookingTime(endBookingData.startTime)} - ${formatBookingTime(endBookingData.endTime)}` : ""}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Request ID</td>
                  <td><span className="fw-600">{endBookingData?.requestId}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <SwAlert variant="warning" icon={<i className="bi bi-exclamation-triangle-fill" />} className="mb-2">
            The booking ends now and the booked devices switch off immediately. You will be billed for the hours used (rounded up to the next hour, minimum 1 hour).
          </SwAlert>
          <div className="SwFormGroup mt-3">
            <label className="SwFormLabel">Reason (Optional)</label>
            <SwTextArea
              className="form-control"
              rows={3}
              placeholder="e.g. Team left early"
              value={endReason}
              onChange={(e) => setEndReason(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
      </SwModal>

      {/* Confirm Cancel Booking Modal */}
      <SwModal
        show={cancelBookingModal}
        title="Cancel Booking"
        className="sw-small-modal ExpandedWidth Mdl_2"
        centered
        keyboard={false}
        onClose={() => setCancelBookingModal(false)}
        backdrop="static"
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3">
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={() => setCancelBookingModal(false)}
            >
              Back
            </SwButton>
            <SwButton
              variant="danger"
              size="sm"
              onClick={handleConfirmCancel}
              loading={cancelLoading}
            >
              Confirm Cancellation
            </SwButton>
          </div>
        }
      >
        <div className="">
          <div className="ConfirmBookingTable">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <td>Floor</td>
                  <td>
                    <span className="fw-600">{cancelBookingData?.floorName}</span>
                  </td>
                </tr>
                <tr>
                  <td>Date</td>
                  <td>
                    <span className="fw-600">
                      {cancelBookingData ? dayjs(cancelBookingData.date).format("MMM DD, YYYY") : ""}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Time</td>
                  <td>
                    <span className="fw-600">
                      {cancelBookingData ? `${formatBookingTime(cancelBookingData.startTime)} - ${formatBookingTime(cancelBookingData.endTime)}` : ""}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="SwFormGroup mt-3">
            <label className="SwFormLabel">Reason for Cancellation (Optional)</label>
            <SwTextArea
              className="form-control"
              rows={3}
              placeholder="Enter reason for cancellation"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>


          <div className="my-3">
            <SwAlert
              variant="warning"
              icon={<i className="bi bi-exclamation-triangle-fill" />}
            >
              This action cannot be undone. The booking will be permanently cancelled.
            </SwAlert>
          </div>
        </div>
      </SwModal>

      {/* Fix #6: Post-booking success modal with Request ID */}
      <SwModal
        show={scheduleSuccessModal}
        title="Booking Confirmed!"
        className="sw-small-modal Mdl_2"
        centered
        keyboard={false}
        onClose={() => {
          setScheduleSuccessModal(false);
          setScheduleResult(null);
        }}
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3">
            <SwButton
              variant="primary"
              size="sm"
              onClick={() => {
                setScheduleSuccessModal(false);
                setScheduleResult(null);
              }}
            >
              Done
            </SwButton>
          </div>
        }
      >
        {scheduleResult && (
          <div>
            <div className="InstantConfirmDetail">
              <p className="fw-600">{scheduleResult.floorName}</p>
              <p>
                {dayjs(scheduleResult.date).format("MMM DD, YYYY")} · {formatBookingTime(scheduleResult.startTime)} – {formatBookingTime(scheduleResult.endTime)}
              </p>
              <p style={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 600, color: "#64748b", background: "#f3f4f6", border: "1px solid #e5e7eb", padding: "2px 8px", borderRadius: "6px", display: "inline-block" }}>
                {scheduleResult.requestId || ""}
              </p>
            </div>
            <div className="my-3">
              <SwAlert
                variant="info"
                icon={<i className="bi bi-info-circle-fill" />}
              >
                You can cancel this booking up to 24 hours before the start time. A confirmation email has been sent. Statement will be generated after utilization.
              </SwAlert>
            </div>
          </div>
        )}
      </SwModal>

      {/* Confirm Instant On Modal */}
      <SwModal
        show={confirmInstantOn}
        title="Service Started!"
        className="sw-small-modal Mdl_2"
        centered
        onClose={() => {
          setConfirmInstantOn(false);
          setInstantOnResult(null);
          setInstantOnEnd(null);
        }}
        keyboard={false}
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3">
            <SwButton
              variant="primary"
              size="sm"
              onClick={() => {
                setConfirmInstantOn(false);
                setInstantOnResult(null);
                setInstantOnEnd(null);
              }}
            >
              Done
            </SwButton>
          </div>
        }
      >
        <div className="">
          <div className="InstantConfirmDetail">
            <p className="fw-600">{floorValue?.label}</p>
            <p>
              Now – {instantOnEnd?.format("h:mm A")}{" "}
              <span className="fw-600">
                ({(() => {
                  if (!instantOnEnd) return "";
                  const isMidnight = instantOnEnd.hour() === 0 && instantOnEnd.minute() === 0;
                  const endForCalc = isMidnight ? dayjs().endOf("day") : instantOnEnd;
                  const diffMins = endForCalc.diff(dayjs(), "minute");
                  const hours = Math.floor(diffMins / 60);
                  const mins = diffMins % 60;
                  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
                  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""}`;
                  return `${mins} min`;
                })()})
              </span>
            </p>
            <p>{instantOnResult?.requestId || ""}</p>
          </div>

          <div className="my-3">
            <SwAlert
              variant="info"
              icon={<i className="bi bi-info-circle-fill" />}
            >
              Service will auto-stop at {instantOnEnd?.format("h:mm A")}.
              Statement generated after end time.
            </SwAlert>
          </div>
        </div>
      </SwModal>

      {/* Past Bookings OffCanvas */}
      <AntDDrawer
        show={showPastBookings}
        onClose={() => setShowPastBookings(false)}
        title="Past Bookings"
        placement="right"
        width={'80%'}
      >
        <h4 >
          View completed bookings and statements
        </h4>
        
        <div className='row mb-3'>
          <div className='col-md-3'>
            <SwSelect
              value={sourceBookingOptions.find(o => o.value === pastBookingsSource)}
              onChange={(option) => setPastBookingsSource(option?.value || "all")}
              options={sourceBookingOptions}
              isClearable={false}
            />
            
          </div>

          <div className='col-md-3'>
            <AntDDatePicker
              picker="month"
              value={pastBookingsMonth}
              onChange={(date) => {
                setPastBookingsMonth(date);
                if (date) fetchPastBookings(date);
              }}
              disabledDate={(current) => current && current.isAfter(dayjs(), 'month')}
              format="MMM YYYY"
              allowClear={false}
            />
          </div>

          <div className='col-md-3'>
            <SwInput
              type="text"
              placeholder="Search Request ID..."
              value={pastBookingsSearch}
              onChange={(e) => setPastBookingsSearch(e.target.value)}
            />
          </div>

          <div className='col-md-3 text-end'>
            <span className="text-muted">
              <span className='SwTextDark fw-700'>{filteredPastBookings.length}</span> booking{filteredPastBookings.length !== 1 ? "s" : ""}
            </span>
          </div>

        </div>

        <AntDTable
          columns={pastBookingsColumns}
          dataSource={filteredPastBookings}
          loading={pastBookingsLoading}
          rowKey="requestId"
          pagination={true}
          pageSize={25}
          clientSideSorting
          bordered
          size="small"
        />
      </AntDDrawer>

      {/* All Upcoming Bookings Modal */}
      <AntDDrawer
        show={showAllUpcoming}
        onClose={() => setShowAllUpcoming(false)}
        title="All Upcoming Bookings"
        placement="right"
        width={'80%'}
      >
        <div className='row mb-3'>
          <div className='col-md-3'>
            <AntDDatePicker
              picker="month"
              value={upcomingBookingsMonth}
              onChange={(date) => {
                setUpcomingBookingsMonth(date);
                if (date) fetchUpcomingBookings(date);
              }}
              disabledDate={(current) => current && current.isBefore(dayjs(), 'month')}
              format="MMM YYYY"
              allowClear={false}
            />
          </div>
          <div className='col-md-3 d-flex align-items-center'>
            <span className="text-muted">
              <span className='SwTextDark fw-700'>{filteredUpcomingBookings.length}</span> booking{filteredUpcomingBookings.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <AntDTable
          columns={upcomingBookingsColumns}
          dataSource={filteredUpcomingBookings}
          loading={upcomingBookingsLoading}
          rowKey="requestId"
          pagination={true}
          pageSize={25}
          clientSideSorting
          bordered
          size="small"
        />
      </AntDDrawer>

      {/* View Statement Modal */}
      <SwModal
        show={!!statementBooking}
        title="Extended Work Hours Statement"
        className="sw-default-modal"
        onClose={() => setStatementBooking(null)}
        centered
        size="lg"
        footer={
          <div className="d-flex justify-content-end align-items-center gap-3">
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={() => setStatementBooking(null)}
            >
              Close
            </SwButton>
          </div>
        }
      >
        {statementBooking && (
          <div>
            {/* Statement Header */}
            <div className="d-flex justify-content-between align-items-start mb-3 pb-3" style={{ borderBottom: "1px solid #e5e7eb" }}>
              <div>
                <div className="fw-700" style={{ fontSize: "15px" }}>Extended Work Hours Statement</div>
                <div className="text-muted" style={{ fontSize: "12px", marginTop: "4px" }}>Auto-generated post-utilization</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 600, color: "#64748b", background: "#f3f4f6", border: "1px solid #e5e7eb", padding: "2px 8px", borderRadius: "6px" }}>
                  {statementBooking.requestId}
                </span>
                <div className="text-muted" style={{ fontSize: "12px", marginTop: "4px" }}>
                  {dayjs(statementBooking.date).format("MMM DD, YYYY")}
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <table className="table table-bordered" style={{ fontSize: "13px" }}>
              <tbody>
                <tr>
                  <td style={{ width: "40%", color: "#64748b", fontWeight: 500 }}>Floor</td>
                  <td className="fw-600">{statementBooking.floorName || "—"}</td>
                </tr>
                <tr>
                  <td style={{ color: "#64748b", fontWeight: 500 }}>Booked Time</td>
                  <td className="fw-600">
                    {formatBookingTime(statementBooking.startTime)} – {formatBookingTime(statementBooking.endTime)}
                    {" "}
                    ({(() => {
                      const start = dayjs(statementBooking.startTime, "HH:mm");
                      const end = dayjs(statementBooking.endTime, "HH:mm");
                      const hours = Math.ceil(end.diff(start, "hour", true));
                      return `${hours} hour${hours > 1 ? "s" : ""}`;
                    })()})
                  </td>
                </tr>
                <tr>
                  <td style={{ color: "#64748b", fontWeight: 500 }}>Default Schedule Ends</td>
                  <td className="fw-600">{statementBooking.defaultEndTime || statementBooking.scheduleEndTime || "—"}</td>
                </tr>
                <tr>
                  <td style={{ color: "#64748b", fontWeight: 500 }}>Billable Extended Hours</td>
                  <td className="fw-600">
                    {formatBookingTime(statementBooking.billableStartTime || statementBooking.startTime)} – {formatBookingTime(statementBooking.billableEndTime || statementBooking.endTime)}
                    {" = "}
                    {(() => {
                      const start = dayjs(statementBooking.billableStartTime || statementBooking.startTime, "HH:mm");
                      const end = dayjs(statementBooking.billableEndTime || statementBooking.endTime, "HH:mm");
                      const hours = Math.ceil(end.diff(start, "hour", true));
                      return `${hours} hour${hours > 1 ? "s" : ""}`;
                    })()}
                  </td>
                </tr>
                <tr>
                  <td style={{ color: "#64748b", fontWeight: 500 }}>Source</td>
                  <td>
                    {statementBooking.source === "cspoc"
                      ? <Tag color="blue">CSPOC</Tag>
                      : <Tag color="purple">{statementBooking.source || "Ops"}</Tag>
                    }
                  </td>
                </tr>
                <tr>
                  <td style={{ color: "#64748b", fontWeight: 500 }}>Status</td>
                  <td>
                    {statementBooking.cancelStatus
                      ? <Tag color="error">Cancelled</Tag>
                      : <Tag color="success">Completed</Tag>
                    }
                  </td>
                </tr>
              </tbody>
            </table>

            {/* AC Profile Section */}
            {(statementBooking.acCount || statementBooking.acType || statementBooking.rate) && (
              <>
                <div className="fw-600 text-muted" style={{ fontSize: "12px", marginBottom: "8px", marginTop: "16px" }}>
                  AC PROFILE — {statementBooking.floorName}
                </div>
                <table className="table table-bordered" style={{ fontSize: "13px" }}>
                  <tbody>
                    {statementBooking.acCount && (
                      <tr>
                        <td style={{ width: "40%", color: "#64748b", fontWeight: 500 }}>AC Count</td>
                        <td>{statementBooking.acCount} units</td>
                      </tr>
                    )}
                    {statementBooking.acType && (
                      <tr>
                        <td style={{ color: "#64748b", fontWeight: 500 }}>AC Type</td>
                        <td>{statementBooking.acType}</td>
                      </tr>
                    )}
                    {statementBooking.rate && (
                      <tr>
                        <td style={{ color: "#64748b", fontWeight: 500 }}>Rate</td>
                        <td>₹{Number(statementBooking.rate).toLocaleString("en-IN")} / hour / unit</td>
                      </tr>
                    )}
                    {statementBooking.calculation && (
                      <tr>
                        <td style={{ color: "#64748b", fontWeight: 500 }}>Calculation</td>
                        <td>{statementBooking.calculation}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {/* Total Amount */}
            {(statementBooking.grossEstimate ?? statementBooking.totalAmount ?? statementBooking.amount) != null && (
              <div className="d-flex justify-content-between align-items-center" style={{ borderTop: "2px solid #1e293b", paddingTop: "10px", marginTop: "6px" }}>
                <span className="fw-700" style={{ fontSize: "14px" }}>Total Amount</span>
                <span className="fw-700" style={{ fontSize: "14px" }}>
                  ₹{Number(statementBooking.grossEstimate ?? statementBooking.totalAmount ?? statementBooking.amount).toLocaleString("en-IN")}
                </span>
              </div>
            )}
          </div>
        )}
      </SwModal>
    </>
  );
};

export default ExtendedV2;
